// IP 合作谈判「核心商务条款」表格（ProTablePlusForm，可复用内包/外包场景）。
// 固定 3 字段（预估金额/付款条件/授权区域，约定前端写死），平铺行；
// 回复列 + 意见反馈列 + 批量编辑标题复用 NegotiationTable/renderCells 工厂，
// 数据收集走外层 editState（key=item_key）。

import React, { forwardRef, memo, useMemo } from 'react';
import { ProTablePlusForm } from '@mpc/common/src/components/ProTablePlusForm';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import { enums } from '#services/api';
import type {
  IpCooperationDemandDetailVoL2Info,
  OptionItemVo,
} from '#services/api';
import {
  ActionCell,
  getOpinionTitle,
  OpinionCell,
  createRenderBatchEditTitle,
  type NegotiationEditState,
  type NegotiationItem,
} from './renderCells';
import CurrencyType from '@mpc/common/src/components/SForm/SFormElement/CurrencyType';

// 核心商务条款固定字段（约定前端写死）：预估金额/付款条件/授权区域
export const CORE_TERM_ITEM_KEYS = [
  'estimated_amount',
  'payment_terms',
  'license_regions',
];
// 核心商务条款中文名（后端 item_name 缺失时兜底）
const CORE_TERM_NAME_MAP: Record<string, string> = {
  estimated_amount: '预估金额',
  payment_terms: '付款条件',
  license_regions: '授权区域',
};

export interface CoreTermTableProps {
  /** 谈判条款列表（negotiation_items，内部取 CORE_TERMS 固定 3 字段） */
  items?: NegotiationItem[];
  /** 二级单信息（l2_info，网易方出价从 estimated_amount/currency/payment_terms/license_regions 派生） */
  l2?: IpCooperationDemandDetailVoL2Info;
  /** 编辑状态（key=item_key） */
  editState?: Record<string, NegotiationEditState>;
  /** 是否可编辑 */
  editable?: boolean;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（仅作用于本表格固定 3 字段） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 发行区域选项（Apollo region.options 动态下发，供授权地区 code→中文名映射） */
  countryRegionOptions?: OptionItemVo[];
  /** 场景模式：external=外包（内容列总宽 700 对齐三表），internal/缺省=内包（保持现状） */
  mode?: 'external' | 'internal';
}

// 网易方出价：从 l2_info 派生（negotiation_items 无 offer_value，后端 IpCooperationNegotiationItemVO 仅 item_key/action/opinion）
const getCoreOfferValue = (
  key: string,
  l2?: IpCooperationDemandDetailVoL2Info,
  countryRegionOptions?: OptionItemVo[]
): string => {
  if (!l2) return '';
  switch (key) {
    case 'estimated_amount':
      return l2.estimated_amount != null
        ? `${l2.estimated_amount}${l2.currency ? ` ${l2.currency}` : ''}`
        : '';
    case 'payment_terms': {
      const opt = enums.IpCooperationPaymentTermsEnum.values().find(
        (o) => o.value === l2.payment_terms
      );
      return opt?.name || l2.payment_terms || '';
    }
    case 'license_regions': {
      // 授权地区 code→中文名：Apollo region.options 动态映射（country_region_options），未知 code 保留原值
      const opts = countryRegionOptions || [];
      return (l2.license_regions || [])
        .map((r) => opts.find((o) => o.value === r)?.name || r)
        .join('/');
    }
    default:
      return '';
  }
};

const CoreTermTableInner: React.FC<CoreTermTableProps> = (props) => {
  const {
    items = [],
    l2,
    editState = {},
    editable = false,
    onEditChange,
    onBatchAction,
    countryRegionOptions,
    mode,
  } = props;
  // 详情条款按 item_key 索引（回显 action/opinion）
  const itemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (items || []).forEach((i) => {
      if (i.item_key) map[i.item_key] = i;
    });
    return map;
  }, [items]);

  // 固定 3 字段平铺行（网易方出价从 l2_info 派生）
  const rows = useMemo(
    () =>
      CORE_TERM_ITEM_KEYS.map((key) => {
        const item = itemsByKey[key];
        return {
          __row_key__: key,
          item_key: key,
          // 条款类别中文优先：后端 item_name 可能存英文字段名（estimated_amount 等，见 IpCooperationL2ServiceImpl 保存时 setItemName(itemKey)），
          // 固定 3 字段一律用前端中文映射兜底，后端 item_name 仅当映射缺失时回退
          item_name: CORE_TERM_NAME_MAP[key] || item?.item_name,
          offer_value: getCoreOfferValue(key, l2, countryRegionOptions),
        };
      }),
    [itemsByKey, l2, countryRegionOptions]
  );

  // 批量编辑标题（列头 Popover，仅针对本表格固定 3 字段）
  const renderBatchEditTitle = createRenderBatchEditTitle({
    editable,
    onBatchAction,
  });

  // 4 列：条款类别 / 网易方出价（预估金额橙色强调）/ 回复 / 意见反馈
  const columns = useMemo<AddGroupFormColumn[]>(
    () => [
      {
        dataIndex: 'item_name',
        title: '条款类别',
        width: 140,
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: { customComp: () => <span>{row.item_name}</span> },
        }),
      },
      {
        dataIndex: 'offer_value',
        title: '网易方出价',
        // 外包内容列总宽统一 700（条款140+出价560，对齐三表回复列 x）；内包保持现状 460
        width: mode === 'external' ? 560 : 460,
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            // 走查：预估金额用 SForm 货币类型只读态渲染（CurrencyType view，financial 千分位 + 万/十万单位 + 币种 + 橙强调）；其他行保持文本
            customComp: () =>
              row.item_key === 'estimated_amount' ? (
                <CurrencyType
                  value={{
                    value: l2?.estimated_amount,
                    currency: l2?.currency,
                  }}
                  view
                  // 单位标记（万/十万…）：CurrencyType showAmountOrder 默认开启
                  showAmountOrder
                  // 币种与金额橙黄色强调（对齐 SForm 货币类型默认色）
                  currencyColor="yellow"
                />
              ) : (
                <span className="text-color-1">{row.offer_value || '-'}</span>
              ),
          },
        }),
      },
      {
        dataIndex: 'action',
        title: renderBatchEditTitle(CORE_TERM_ITEM_KEYS),
        // 回复列固定宽（走查：编辑态 180px 放三个 Medium Tag / 只读态 100px 放单 Tag）
        width: editable ? 180 : 100,
        // 外包走查 recZIyp：回复列右侧描边缺失（意见反馈列弹性无宽，verticalBorder 不渲染该侧边框）→ 回复列 th/td 加右边框（仅外包模式，内包保持现状）
        ...(mode === 'external'
          ? {
              onCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
              onHeaderCell: () => ({
                style: { borderRight: '1px solid rgb(223, 224, 229)' },
              }),
            }
          : {}),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            customComp: ActionCell,
            row,
            itemsByKey,
            editState,
            editable,
            readOnly: !editable,
            onEditChange,
          },
        }),
      },
      {
        dataIndex: 'opinion',
        title: getOpinionTitle(),
        editRender: ({ row }) => ({
          name: 'custom-component',
          editable: true,
          showReadonlyState: true,
          props: {
            customComp: OpinionCell,
            row,
            itemsByKey,
            editState,
            editable,
            readOnly: !editable,
            onEditChange,
          },
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsByKey, editState, editable, onEditChange, onBatchAction, mode]
  );

  return (
    <ProTablePlusForm
      value={rows}
      onChange={() => {}}
      columns={columns}
      rowKey="__row_key__"
      verticalBorder
      // 走查：右侧详情内容需自适应——表格宽度铺满容器，意见反馈列（无固定宽）弹性占满剩余，避免右侧留白
      scroll={{ x: '100%' }}
      // 表格级只读：TableExtend custom-component 注册会用 ProTable 表格级 editable 覆盖单元格 editRender 的 editable，
      // 故须用 readOnly 控制（readOnly=true → 表格级 editable=false → 回复/意见列只读）
      readOnly={!editable}
    />
  );
};

// FormModel 字段渲染（SFormItem/Form.Item）会注入 ref；展示型表格无需真实 ref，
// forwardRef 接受并忽略，避免"函数组件不能接收 ref"警告（对齐 BenefitConfigNew 范式）。
// memo 包裹：props（items/l2/editState/回调）稳定时跳过重渲染，隔离父组件无关重绘
const CoreTermTable = memo(
  forwardRef<unknown, CoreTermTableProps>((props, ref) => {
    void ref;
    return <CoreTermTableInner {...props} />;
  })
);

export default CoreTermTable;
